// MARK://dhk8/pos/features/security/PinPromptSheet
// Lane #172 — Manager-PIN prompt en DOS PASOS + razón opcional.
//
// Paso 1: pinId (6 dígitos — ID del gerente, globalmente único).
// Paso 2: pin   (6 dígitos — clave del gerente).
// Si `options.requireReason: true` se pide además un motivo libre (textarea
// obligatoria) antes de cerrar — el resultado lleva `reason` poblado para
// audit + event payload (forceClear, cortesía, etc).
//
// Mounted once at the shell level (PosOverlays); cualquier handler llama
// `requestManagerPin(action, options)` y obtiene `{ approved, pinId?, reason? }`.

import { useEffect, useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { NumPad } from '@dockethub/ui-restaurant'

import BottomSheet from '../pos/BottomSheet.js'
import { usePinGateStore } from './pin-gate-store.js'
import {
  GATED_ACTION_LABEL, MANAGER_PIN_LENGTH, PIN_GATE_DEMO_MODE, DEMO_MANAGER_PIN,
} from '../../lib/security-policy.js'

type Step = 'pinId' | 'pin' | 'reason'

export default function PinPromptSheet() {
  const open = usePinGateStore((s) => s.open)
  const action = usePinGateStore((s) => s.action)
  const options = usePinGateStore((s) => s.options)
  const error = usePinGateStore((s) => s.error)
  const busy = usePinGateStore((s) => s.busy)
  const submit = usePinGateStore((s) => s.submit)
  const cancel = usePinGateStore((s) => s.cancel)

  const requireReason = options.requireReason === true
  const [step, setStep] = useState<Step>('pinId')
  const [pinId, setPinId] = useState('')
  const [pin, setPin] = useState('')
  const [reason, setReason] = useState('')

  // Reset todo al re-abrir o cuando cambia la acción.
  useEffect(() => {
    if (open) { setStep('pinId'); setPinId(''); setPin(''); setReason('') }
  }, [open, action])

  // Si el backend rechazó el PIN → volver al paso 1 (puede ser que el gerente
  // se equivocó de cuenta) y limpiar la clave.
  useEffect(() => {
    if (error) { setStep('pinId'); setPin('') }
  }, [error])

  // Avance automático pinId → pin cuando completa los 6 dígitos.
  useEffect(() => {
    if (!open || busy) return
    if (step === 'pinId' && pinId.length === MANAGER_PIN_LENGTH) {
      setStep('pin')
    }
  }, [pinId, step, open, busy])

  // Cuando ambos PINs están completos:
  //   - sin razón requerida → submit directo
  //   - con razón requerida → pasar al paso 'reason'
  useEffect(() => {
    if (!open || busy) return
    if (step === 'pin' && pin.length === MANAGER_PIN_LENGTH) {
      if (requireReason) {
        setStep('reason')
      } else {
        void submit({ pinId, pin })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, step, open, busy, requireReason])

  function handleConfirmReason() {
    const r = reason.trim()
    if (r.length < 4) return
    void submit({ pinId, pin, reason: r })
  }

  /**
   * Lane #172.B (2026-05-26) — keyboard input global. El NumPad on-screen
   * no tiene focus (no es un <input>), así que un teclado físico / pinpad
   * USB conectado al equipo ignoraba los dígitos: el cajero solo podía
   * tocar la pantalla. Aquí enganchamos un listener `keydown` mientras el
   * sheet esté abierto: dígitos 0-9 alimentan el step actual; Backspace
   * borra; Enter dispara confirm; Escape cancela. No interferimos con el
   * paso 'reason' porque ahí el textarea ya captura el teclado.
   */
  useEffect(() => {
    if (!open || busy) return
    if (step === 'reason') return
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'Escape') { e.preventDefault(); cancel(); return }
      if (e.key === 'Enter')  { e.preventDefault(); handleConfirmFromNumpad(); return }
      if (e.key === 'Backspace') {
        e.preventDefault()
        if (step === 'pinId') setPinId((v) => v.slice(0, -1))
        else if (step === 'pin') setPin((v) => v.slice(0, -1))
        return
      }
      // Tecla numérica (normal o numpad). e.key es '0'..'9' en ambos casos.
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault()
        if (step === 'pinId') setPinId((v) => (v.length < MANAGER_PIN_LENGTH ? v + e.key : v))
        else if (step === 'pin') setPin((v) => (v.length < MANAGER_PIN_LENGTH ? v + e.key : v))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('keydown', onKey) }
    // handleConfirmFromNumpad cierra sobre pinId/pin/step actuales; los
    // useEffects de auto-advance ya cubren la transición tras alcanzar 6.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, busy, step])

  /**
   * Lane #172-fix — el botón check del NumPad ahora dispara avance/submit
   * explícito. Antes era inerte (NumPad sin onConfirm) y dependía SÓLO del
   * auto-submit por longitud, lo que confundía al staff que pulsaba el check
   * con menos de 6 dígitos esperando feedback.
   */
  function handleConfirmFromNumpad(): void {
    if (busy) return
    if (step === 'pinId' && pinId.length === MANAGER_PIN_LENGTH) {
      setStep('pin')
      return
    }
    if (step === 'pin' && pin.length === MANAGER_PIN_LENGTH) {
      if (requireReason) {
        setStep('reason')
      } else {
        void submit({ pinId, pin })
      }
    }
  }

  const subtitle = action ? GATED_ACTION_LABEL[action] : null
  const contextLabel = options.contextLabel

  const titleByStep: Record<Step, string> = {
    pinId: 'ID de gerente',
    pin: 'Clave de gerente',
    reason: 'Motivo',
  }

  const heading = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <ShieldCheck size={16} aria-hidden /> Autorización — {titleByStep[step]}
    </span>
  )

  const reasonValid = reason.trim().length >= 4

  return (
    <BottomSheet
      open={open}
      onClose={() => { if (!busy) cancel() }}
      title={heading}
      ariaLabel="Autorización de gerente"
      heightFraction={requireReason ? 0.82 : 0.66}
      footer={
        <div className="dhk8-pingate__foot">
          {step === 'pin' && (
            <button
              type="button"
              className="dhk8-pingate__btn dhk8-pingate__btn--ghost"
              onClick={() => { setStep('pinId'); setPin('') }}
              disabled={busy}
            >
              Cambiar gerente
            </button>
          )}
          {step === 'reason' && (
            <button
              type="button"
              className="dhk8-pingate__btn dhk8-pingate__btn--primary"
              onClick={handleConfirmReason}
              disabled={busy || !reasonValid}
            >
              Confirmar
            </button>
          )}
          <button
            type="button"
            className="dhk8-pingate__btn dhk8-pingate__btn--ghost"
            onClick={cancel}
            disabled={busy}
          >
            Cancelar
          </button>
        </div>
      }
    >
      <div className="dhk8-pingate">
        <p className="dhk8-pingate__lead">
          {step === 'pinId' && <>Ingresa el <strong>ID</strong> del gerente{subtitle ? <> para <strong>{subtitle.toLowerCase()}</strong></> : null}.</>}
          {step === 'pin' && <>Ingresa la <strong>clave</strong> del gerente.</>}
          {step === 'reason' && <>Anota el motivo (obligatorio) para <strong>{subtitle?.toLowerCase()}</strong>.</>}
        </p>
        {contextLabel && (
          <p className="dhk8-pingate__ctx">{contextLabel}</p>
        )}
        {PIN_GATE_DEMO_MODE && step !== 'reason' && (
          <p className="dhk8-pingate__hint">(demo: PIN {DEMO_MANAGER_PIN})</p>
        )}

        {step !== 'reason' && (
          <>
            <div className="dhk8-pingate__dots" aria-hidden="true">
              {Array.from({ length: MANAGER_PIN_LENGTH }).map((_, i) => {
                const len = step === 'pinId' ? pinId.length : pin.length
                return (
                  <span key={i} className={`dhk8-pingate__dot${i < len ? ' dhk8-pingate__dot--on' : ''}`} />
                )
              })}
            </div>
            {error && step === 'pinId' && <div className="dhk8-pingate__err" role="alert">{error}</div>}
            <div className="dhk8-pingate__pad">
              <NumPad
                value={step === 'pinId' ? pinId : pin}
                onChange={(v) => {
                  if (busy) return
                  const trimmed = v.slice(0, MANAGER_PIN_LENGTH)
                  if (step === 'pinId') setPinId(trimmed)
                  else setPin(trimmed)
                }}
                onConfirm={handleConfirmFromNumpad}
                maxLength={MANAGER_PIN_LENGTH}
                mode="pin"
                disabled={busy}
                ariaLabel={step === 'pinId' ? 'ID de gerente' : 'PIN de gerente'}
              />
            </div>
          </>
        )}

        {step === 'reason' && (
          <>
            <textarea
              className="dhk8-pingate__reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: cliente abandonó la mesa sin pagar, cortesía por inconformidad…"
              rows={4}
              maxLength={500}
              autoFocus
              disabled={busy}
            />
            <p className="dhk8-pingate__hint">
              {reason.trim().length < 4 ? 'Mínimo 4 caracteres' : `${reason.trim().length} caracteres`}
            </p>
          </>
        )}
      </div>
      <style>{styles}</style>
    </BottomSheet>
  )
}

const styles = `
  .dhk8-pingate { display:flex; flex-direction:column; align-items:center; gap:var(--pos-space-3,12px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-pingate__lead { margin:0; text-align:center; font-size:var(--pos-text-md,15px); color:var(--pos-fg-secondary,#B6BFB8); line-height:1.4; }
  .dhk8-pingate__lead strong { color:var(--pos-fg,#E8EFE9); font-weight:var(--pos-weight-semibold,600); }
  .dhk8-pingate__ctx { margin:0; font-size:var(--pos-text-sm,13px); color:var(--pos-fg-tertiary,#7E8C82); text-align:center; padding:var(--pos-space-1,4px) var(--pos-space-3,12px); background:var(--pos-elevated,#1E2520); border-radius:var(--pos-radius-sm,6px); }
  .dhk8-pingate__hint { margin:0; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); font-variant-numeric:tabular-nums; }
  .dhk8-pingate__dots { display:flex; gap:var(--pos-space-3,12px); align-items:center; justify-content:center; }
  .dhk8-pingate__dot { width:12px; height:12px; border:1.5px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; border-radius:var(--radius-dot); transition:background var(--pos-duration-fast,120ms) ease, border-color var(--pos-duration-fast,120ms) ease, transform var(--pos-duration-fast,120ms) ease; }
  .dhk8-pingate__dot--on { background:var(--pos-tint,#1F8B3A); border-color:var(--pos-tint,#1F8B3A); transform:scale(1.08); }
  .dhk8-pingate__err { width:100%; text-align:center; color:var(--pos-danger,#E5484D); background:color-mix(in oklch, var(--pos-danger,#E5484D) 14%, transparent); border:1px solid color-mix(in oklch, var(--pos-danger,#E5484D) 40%, transparent); border-radius:var(--pos-radius-md,8px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); font-size:var(--pos-text-sm,13px); }
  .dhk8-pingate__pad { width:100%; max-width:280px; }
  .dhk8-pingate__reason { width:100%; max-width:480px; min-height:120px; resize:vertical; font:inherit; font-size:var(--pos-text-md,15px); padding:var(--pos-space-3,12px); border-radius:var(--pos-radius-md,8px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); }
  .dhk8-pingate__reason:focus { outline:2px solid var(--pos-tint,#1F8B3A); outline-offset:1px; }
  .dhk8-pingate__foot { display:flex; gap:var(--pos-space-2,8px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-pingate__btn { flex:1; min-height:var(--pos-touch-target,44px); display:inline-flex; align-items:center; justify-content:center; border-radius:var(--pos-radius-md,8px); font:inherit; font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-medium,500); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-pingate__btn:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-pingate__btn--ghost { border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; color:var(--pos-fg,#E8EFE9); }
  .dhk8-pingate__btn--ghost:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-pingate__btn--primary { border:1px solid var(--pos-tint,#1F8B3A); background:var(--pos-tint,#1F8B3A); color:#fff; }
  .dhk8-pingate__btn--primary:hover:not(:disabled) { filter:brightness(1.08); }
`